import { Collection, Space } from '@tribeplatform/gql-client/types'

export const NO_COLLECTION_ID = 'no-collection'

export enum CollectionMenuViewStyle {
  Simple = 'simple',
  Collapsible = 'collapsible',
  Heading = 'heading',
}

export enum CollectionMenuSpaceFilter {
  All = 'all',
  Joined = 'joined',
  NotJoined = 'notJoined',
}

export type CollectionMenuSpaceItem = {
  id: string
  enabled: boolean
}

export type CollectionMenuItem = CollectionMenuSpaceItem & {
  spaces?: CollectionMenuSpaceItem[]
}

export type CollectionMenuDetailedItem = CollectionMenuItem & {
  name: string
}

export type CollectionMenuDetailedSpaceItem = CollectionMenuSpaceItem & {
  space: Space
}

export type CollectionMenuProps = {
  items?: CollectionMenuItem[]
  viewStyle: CollectionMenuViewStyle
  spaceFilter?: CollectionMenuSpaceFilter
}

export type NoCollectionEntry = {
  id: typeof NO_COLLECTION_ID
  name: string
  customOrderingIndex: number
  spaces?: {
    nodes: Space[]
    totalCount: number
  }
}

export type CollectionMenuEntry = Collection | NoCollectionEntry

export const isNoCollectionEntry = (
  entry: CollectionMenuEntry,
): entry is NoCollectionEntry => entry.id === NO_COLLECTION_ID

/**
 * A locale group surfaces as a single nav entry. The space shown is the variant
 * matching the user's locale, falling back to the group's primary space when
 * the user's locale has no variant. Spaces outside a locale group (or whose
 * `localeGroup` field wasn't fetched) pass through unchanged. The collapsed
 * entry is emitted at the first-seen group member's position, so a group is
 * never dropped even when its primary isn't in the list (e.g. the "Joined"
 * filter, where the member joined only their locale variant).
 */
const isLocaleGroupMember = (space: Space): boolean =>
  !!space.localeGroupId && !!space.localeGroup

const normalizeLocale = (locale: string): string =>
  locale.trim().toLowerCase().replace('_', '-')

/**
 * Ranks how well a space's locale matches the requested UI locale, tolerating
 * case and region granularity: the UI locale (`intl.locale`) can carry a region
 * subtag like `en-US`/`pt-BR` while `space.locale` may be a bare or
 * differently-cased subtag. Returns 2 for an exact match after normalization,
 * 1 for a shared primary language subtag (e.g. `en` vs `en-US`), 0 for none.
 */
const localeMatchScore = (spaceLocale?: string, locale?: string): number => {
  if (!spaceLocale || !locale) {
    return 0
  }
  const normalizedSpace = normalizeLocale(spaceLocale)
  const normalizedLocale = normalizeLocale(locale)
  if (normalizedSpace === normalizedLocale) {
    return 2
  }
  if (normalizedSpace.split('-')[0] === normalizedLocale.split('-')[0]) {
    return 1
  }
  return 0
}

/** Picks the better of two members of the same group to represent it. */
const preferredGroupSpace = (
  current: Space,
  candidate: Space,
  locale?: string,
): Space => {
  const currentScore = localeMatchScore(current.locale, locale)
  const candidateScore = localeMatchScore(candidate.locale, locale)
  if (candidateScore > currentScore) {
    return candidate
  }
  if (candidateScore < currentScore) {
    return current
  }
  // Equal locale match: fall back to the primary. Both members belong to the
  // same group, so they share one primary space.
  const primarySpaceId = current.localeGroup?.primarySpaceId
  const currentIsPrimary = current.id === primarySpaceId
  const candidateIsPrimary = candidate.id === primarySpaceId
  if (candidateIsPrimary && !currentIsPrimary) {
    return candidate
  }
  return current
}

export const getCollectionMenuEntrySpaces = (
  entry: CollectionMenuEntry,
  locale?: string,
): Space[] => {
  const spaces = isNoCollectionEntry(entry)
    ? (entry.spaces?.nodes ?? [])
    : (entry.spaces?.edges?.map(({ node }) => node) ?? [])

  return collapseLocaleGroupSpaces(spaces, locale)
}

export const collapseLocaleGroupSpaces = (
  spaces: Space[],
  locale?: string,
): Space[] => {
  // Per locale group, pick the space to display: prefer the user's-locale
  // variant, else the primary, else the first member seen.
  const chosenByGroup = spaces.reduce<Map<string, Space>>((acc, space) => {
    if (isLocaleGroupMember(space)) {
      const groupId = space.localeGroupId as string
      const current = acc.get(groupId)
      acc.set(
        groupId,
        current ? preferredGroupSpace(current, space, locale) : space,
      )
    }
    return acc
  }, new Map())

  const emittedGroups = new Set<string>()
  return spaces.reduce<Space[]>((acc, space) => {
    if (!isLocaleGroupMember(space)) {
      acc.push(space)
      return acc
    }
    const groupId = space.localeGroupId as string
    if (!emittedGroups.has(groupId)) {
      emittedGroups.add(groupId)
      acc.push(chosenByGroup.get(groupId) ?? space)
    }
    return acc
  }, [])
}
